import { useEffect } from "react";
import "./invoice-section-style.css";

//電子發票區塊 — 給 /checkout 使用
//  受控組件：value / onChange 都由 parent 傳
//  4 種發票類型（依綠界 B2C 支援）：
//    paper    → 會員電子發票（Print=0，寄用戶 email）
//    carrier  → 手機條碼載具（/XXXXXXX）
//    donation → 捐贈（慈濟 168001 / 家扶 15001）
//    b2b      → 統編三聯式（8 位數字 + 抬頭）
//捐贈固定慈濟基金會（愛心碼 168001）；未來要開放多選再擴充陣列
export const DONATION_ORGS = [{ code: "168001", label: "慈濟基金會" }];

//手機條碼格式：斜線開頭 + 7 位（英數大寫、+ - .）
const CARRIER_PATTERN = /^\/[0-9A-Z+\-.]{7}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TAX_ID_PATTERN = /^\d{8}$/;

//初始值（給 parent 用）
export const defaultInvoiceValue = (email = "") => ({
  type: "paper",
  email,
  carrierNumber: "",
  donationCode: DONATION_ORGS[0].code,
  customerIdentifier: "",
  companyName: "",
});

//前端驗證：回傳 { valid, message }
export const validateInvoice = (v) => {
  if (!v?.email || !EMAIL_PATTERN.test(v.email)) {
    return { valid: false, message: "請填寫正確的 Email" };
  }
  if (v.type === "carrier") {
    const num = (v.carrierNumber || "").toUpperCase();
    if (!CARRIER_PATTERN.test(num)) {
      return {
        valid: false,
        message: "手機條碼格式錯誤（例：/ABC1234）",
      };
    }
  } else if (v.type === "donation") {
    if (!DONATION_ORGS.some((o) => o.code === v.donationCode)) {
      return { valid: false, message: "請選擇捐贈單位" };
    }
  } else if (v.type === "b2b") {
    if (!TAX_ID_PATTERN.test(v.customerIdentifier || "")) {
      return { valid: false, message: "統編需為 8 位數字" };
    }
    if (!(v.companyName || "").trim()) {
      return { valid: false, message: "請填寫公司抬頭" };
    }
  }
  return { valid: true, message: "" };
};

//送出綠界前先組成後端接受的 payload
export const buildInvoicePayload = (v) => {
  const base = { type: v.type, email: v.email };
  if (v.type === "carrier") {
    return { ...base, carrierNumber: (v.carrierNumber || "").toUpperCase() };
  }
  if (v.type === "donation") {
    return { ...base, donationCode: v.donationCode };
  }
  if (v.type === "b2b") {
    return {
      ...base,
      customerIdentifier: v.customerIdentifier,
      companyName: v.companyName.trim(),
    };
  }
  return base;
};

const InvoiceSection = ({ value, onChange, defaultEmail = "" }) => {
  //parent 沒帶 email 時第一次 render 把 defaultEmail 塞進去
  useEffect(() => {
    if (!value?.email && defaultEmail) {
      onChange?.({ ...value, email: defaultEmail });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail]);

  const setField = (patch) => onChange?.({ ...value, ...patch });

  const isActive = (t) => value?.type === t;

  return (
    <div className="invoice-section">
      <div className="invoice-options">
        {/* 會員電子發票 */}
        <label
          className={`invoice-option${isActive("paper") ? " active" : ""}`}
        >
          <input
            type="radio"
            name="invoice-type"
            value="paper"
            checked={isActive("paper")}
            onChange={() => setField({ type: "paper" })}
          />
          <span className="label-text">會員電子發票</span>
        </label>

        {/* 手機條碼載具 */}
        <label
          className={`invoice-option${isActive("carrier") ? " active" : ""}`}
        >
          <input
            type="radio"
            name="invoice-type"
            value="carrier"
            checked={isActive("carrier")}
            onChange={() => setField({ type: "carrier" })}
          />
          <span className="label-text">手機條碼載具</span>
          {isActive("carrier") && (
            <input
              className="invoice-input inline"
              type="text"
              placeholder="/ABC1234"
              maxLength={8}
              value={value?.carrierNumber || ""}
              onChange={(e) =>
                setField({ carrierNumber: e.target.value.toUpperCase() })
              }
            />
          )}
        </label>

        {/* 捐贈 */}
        <label
          className={`invoice-option${isActive("donation") ? " active" : ""}`}
        >
          <input
            type="radio"
            name="invoice-type"
            value="donation"
            checked={isActive("donation")}
            onChange={() => setField({ type: "donation" })}
          />
          <span className="label-text">捐贈發票</span>
        </label>

        {/* 統編（三聯式） */}
        <label
          className={`invoice-option${isActive("b2b") ? " active" : ""}`}
        >
          <input
            type="radio"
            name="invoice-type"
            value="b2b"
            checked={isActive("b2b")}
            onChange={() => setField({ type: "b2b" })}
          />
          <span className="label-text">統一編號</span>
          {isActive("b2b") && (
            <div className="b2b-fields">
              <input
                className="invoice-input"
                type="text"
                inputMode="numeric"
                placeholder="統編 8 位數字"
                maxLength={8}
                value={value?.customerIdentifier || ""}
                onChange={(e) =>
                  setField({
                    customerIdentifier: e.target.value.replace(/\D/g, ""),
                  })
                }
              />
              <input
                className="invoice-input"
                type="text"
                placeholder="公司抬頭"
                maxLength={60}
                value={value?.companyName || ""}
                onChange={(e) => setField({ companyName: e.target.value })}
              />
            </div>
          )}
        </label>
      </div>

      <div className="invoice-email-row">
        <label>Email 通知</label>
        <input
          className="invoice-input"
          type="email"
          placeholder="用於接收電子發票通知"
          value={value?.email || ""}
          onChange={(e) => setField({ email: e.target.value })}
        />
      </div>
    </div>
  );
};

export default InvoiceSection;
